/**
 * Filing on the GST portal, for one period (D-07..D-09, D-18; FR-E33, FR-E34, FR-E38).
 *
 * On Returns for a GSTR-1 or a GSTR-1A, and on GSTR-3B's period, where the strip is the 3B's
 * own (D-10): save → GSTN's figures beside ours → offset → EVC OTP → file. What it shows comes from
 * `GET /periods/{id}/filing`: the next step in a sentence, then every submission in words —
 * never a reference id, a status code or a hash.
 *
 * The buttons are the service's `next`, for a person holding `filing.can_update` (the
 * manager's, ADR-0037 ND-E12). Submit, proceed and file go through `IrreversibleConfirm`, which
 * asks for a fresh two-step code (XC-01). A DSC client's panel stops after the submit and says
 * what to do on the portal (ND-E3, D-09); the filing is then recorded below as for a return
 * filed by hand. **Nothing here claims a filing worked against GSTN** — the lane is proven
 * only against recorded fixtures until D-15.
 */

import { Alert, Button, Card, Input } from '@fiveox/ui'
import { KeyRound, RefreshCw, Send } from 'lucide-react'
import { Link } from 'react-router'
import { useCallback, useEffect, useState } from 'react'

import { FILING, PERIOD, useEvaluation } from '../lib/access'
import { type PeriodFilingView, filing, periodFiling } from '../lib/api'
import { clientPath } from '../lib/clientScope'
import { refusalWords } from '../lib/filingLane'
import { IRREVERSIBLE, type PanelStep, needsSession, staleWords, stepsFor, submissionHeading } from '../lib/periodFiling'
import { IrreversibleConfirm } from './IrreversibleConfirm'

/** The states the panel is shown in: from the client's approval on. */
export const FILING_STATES: ReadonlySet<string> = new Set(['client_approved', 'filed', 'locked'])

type Irreversible = 'submit' | 'proceed' | 'offset' | 'file'

const CONFIRM_COPY: Record<Irreversible, { title: string; body: string; label: string }> = {
  submit: {
    title: 'Submit this return on the GST portal?',
    body:
      'Submitting freezes the return on the portal: the saved data can no longer be changed ' +
      'there. It does not file the return; filing is the next step.',
    label: 'Submit on the portal',
  },
  proceed: {
    title: 'Proceed to file?',
    body:
      'The portal prepares the return for filing. Nothing is filed until the EVC OTP is ' +
      'typed and the return is filed in the next step.',
    label: 'Proceed to file',
  },
  offset: {
    title: 'Offset this GSTR-3B’s liability on the GST portal?',
    body:
      'The portal pays the liability from the electronic credit and cash ledgers, head by ' +
      'head, as 6.1 sets it off. A payment cannot be taken back. ProGST has checked that the ' +
      'cash ledger holds the cash under each head; it does not file the return.',
    label: 'Offset the liability',
  },
  file: {
    title: 'File this return with EVC?',
    body:
      'The return is filed on the GST portal with the OTP the authorised signatory received. ' +
      'An acknowledgment number is issued and the filing cannot be taken back; a correction ' +
      'after this is an amendment.',
    label: 'File the return',
  },
}

export function PeriodFilingPanel({
  periodId,
  clientId,
  onChanged,
}: {
  periodId: string
  clientId: string
  /** Called after a step moved the period (filed), so the screen re-reads it. */
  onChanged?: () => void
}) {
  const { can } = useEvaluation()
  const [view, setView] = useState<PeriodFilingView | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [evc, setEvc] = useState('')
  const [confirming, setConfirming] = useState<Irreversible | null>(null)

  const load = useCallback(async () => {
    try {
      setView((await periodFiling.view(periodId)).data)
    } catch (e) {
      setError(refusalWords(e))
    }
  }, [periodId])

  useEffect(() => {
    setView(null)
    setError(null)
    setEvc('')
    void load()
  }, [load])

  async function act(run: () => Promise<{ data: PeriodFilingView }>) {
    setBusy(true)
    setError(null)
    try {
      const next = (await run()).data
      setView(next)
      if (next.next === 'filed' || next.next === 'confirm' || next.next === 'not_approved') {
        onChanged?.()
      }
    } catch (e) {
      setError(refusalWords(e))
      void load()
    } finally {
      setBusy(false)
    }
  }

  if (!view) {
    return error ? <Alert tone="error">{error}</Alert> : null
  }

  const mayFile = can(PERIOD, 'can_update') && can(FILING, 'can_update')
  const steps = stepsFor(view, mayFile)
  const threeB = view.return_type === 'GSTR3B'

  function stepButton(step: PanelStep) {
    switch (step) {
      case 'save':
        return (
          <Button key={step} size="sm" disabled={busy} onClick={() => void act(() => (threeB ? periodFiling.saveThreeB(periodId) : periodFiling.save(periodId)))}>
            <Send size={15} /> Save to the GST portal
          </Button>
        )
      case 'autocalc':
        return (
          <Button
            key={step}
            size="sm"
            disabled={busy}
            onClick={() =>
              void act(async () => {
                await filing.fetchGstr3bAutocalc(periodId)
                onChanged?.()
                return periodFiling.view(periodId)
              })
            }
          >
            <RefreshCw size={15} /> Fetch GSTN&rsquo;s figures
          </Button>
        )
      case 'confirm':
        return (
          <Button key={step} size="sm" disabled={busy} onClick={() => void act(() => periodFiling.confirm(periodId))}>
            <RefreshCw size={15} /> Read the filing back from the portal
          </Button>
        )
      case 'evc':
        return (
          <Button key={step} size="sm" disabled={busy} onClick={() => void act(() => periodFiling.evcOtp(periodId))}>
            <Send size={15} /> Send the EVC OTP to the signatory
          </Button>
        )
      default:
        return null
    }
  }

  return (
    <Card className="mb-4 p-4" data-testid="period-filing">
      <p className="gst-panel-h mb-2">Filing on the GST portal</p>
      {view.lane.mode === 'off' && view.lane.sentence && (
        <p className="gst-meta mb-2">{view.lane.sentence}</p>
      )}
      <p className="mb-2">{view.next_words}</p>
      <p className="gst-meta mb-3">{view.signature_words}</p>
      {view.confirmed === false && (
        <p className="gst-meta mb-3">
          The portal&rsquo;s list of filed returns does not show it yet; read it back again shortly.
        </p>
      )}
      {error && (
        <Alert tone="error" className="mb-3">
          {error}
        </Alert>
      )}

      {needsSession(view) && mayFile && (
        <p className="gst-meta mb-3">
          There is no live GST portal session for this client.{' '}
          <Link to={clientPath(clientId, 'filing')} className="gst-link">
            Open one on the Filing screen
          </Link>{' '}
          first.
        </p>
      )}
      {!mayFile && view.lane.mode !== 'off' && view.next !== 'not_in_lane' && (
        <p className="gst-meta mb-3">Sending a return to the GST portal is for the firm&rsquo;s GST manager.</p>
      )}

      {steps.length > 0 && (
        <div className="mb-3 flex flex-wrap items-end gap-2">
          {steps.map((step) =>
            IRREVERSIBLE.has(step) && step !== 'file' ? (
              <Button key={step} size="sm" disabled={busy} onClick={() => setConfirming(step as Irreversible)}>
                {CONFIRM_COPY[step as Irreversible].label}
              </Button>
            ) : step === 'file' ? (
              <form
                key={step}
                className="flex flex-wrap items-end gap-2"
                onSubmit={(e) => {
                  e.preventDefault()
                  if (evc.length >= 4) setConfirming('file')
                }}
              >
                <Input
                  label="EVC OTP the signatory received"
                  value={evc}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={8}
                  onChange={(e) => setEvc(e.target.value.replace(/[^0-9]/g, ''))}
                />
                <Button type="submit" size="sm" disabled={busy || evc.length < 4}>
                  <KeyRound size={15} /> {threeB ? 'File the GSTR-3B' : 'File the return'}
                </Button>
              </form>
            ) : (
              stepButton(step)
            ),
          )}
        </div>
      )}

      {view.submissions.length > 0 && (
        <ul className="space-y-2" aria-label="What was sent to the GST portal">
          {view.submissions.map((s) => (
            <li key={s.id} className="border-subtle rounded border p-2">
              <p className="gst-meta">{submissionHeading(s)}</p>
              <p>{s.words}</p>
              {staleWords(s) && <p className="gst-meta">{staleWords(s)}</p>}
              {s.errors.length > 0 && (
                <ul className="gst-meta list-disc pl-5">
                  {s.errors.map((line, i) => (
                    <li key={i}>{line}</li>
                  ))}
                </ul>
              )}
              {s.can_check && mayFile && (
                <Button
                  size="sm"
                  disabled={busy}
                  onClick={() => void act(() => periodFiling.check(periodId, s.id))}
                >
                  <RefreshCw size={15} /> Check now
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}

      {confirming && (
        <IrreversibleConfirm
          open
          onClose={() => setConfirming(null)}
          title={CONFIRM_COPY[confirming].title}
          confirmLabel={CONFIRM_COPY[confirming].label}
          words={refusalWords}
          run={async (assertion) => {
            const step = confirming
            const otp = evc
            const next = (
              step === 'submit'
                ? await periodFiling.submit(periodId, assertion)
                : step === 'proceed'
                  ? await periodFiling.proceed(periodId, assertion)
                  : step === 'offset'
                    ? await periodFiling.offsetThreeB(periodId, assertion)
                    : threeB
                      ? await periodFiling.fileThreeB(periodId, otp, assertion)
                      : await periodFiling.file(periodId, otp, assertion)
            ).data
            setEvc('')
            setView(next)
            setConfirming(null)
            if (step === 'file') onChanged?.()
          }}
        >
          <p>{CONFIRM_COPY[confirming].body}</p>
        </IrreversibleConfirm>
      )}
    </Card>
  )
}
